/**
 * Where everything on the screen goes, for every size of screen.
 *
 * Somebody arranges their interface on the screen in front of them. That
 * arrangement is kept for that size of screen and comes back exactly whenever
 * the game is that size again: full screen on the desk, a tall window on the
 * second monitor, and full screen again, each the way it was left.
 *
 * A size nobody has arranged for is worked out from the nearest thing that was.
 * Each window keeps its place against the edges it was near: the bag that sat
 * in the bottom right corner stays that far from the bottom right corner, and
 * something in the middle stays in the middle. Two windows side by side in a
 * corner move together and stay side by side, rather than being pushed onto
 * each other one at a time by the new edges. Nothing worked out this way is
 * written down; only what somebody moves or resizes by hand is, so the layout
 * they made is never overwritten by a guess at a different size.
 *
 * On top of that, layouts can be kept by name and put back, and a named one can
 * be used automatically whenever the screen is wide or whenever it is tall.
 */

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Screen {
  width: number;
  height: number;
}

/** A window put somewhere by hand, and the size of screen it was put on. */
interface Placed {
  x: number;
  y: number;
  width: number;
  height: number;
  screenW: number;
  screenH: number;
}

/** A window resized by hand, and the size of screen it was resized on. */
interface Sized {
  width: number;
  height: number;
  screenW: number;
  screenH: number;
}

type Entry = Placed | Sized;

/** Which kind of screen a named layout is used on without being asked. */
export type LayoutAuto = 'wide' | 'tall' | null;

export interface LayoutProfile {
  name: string;
  auto: LayoutAuto;
  entries: Record<string, Entry>;
}

interface Store {
  /** What was arranged by hand, per size of screen, and when it last was. */
  screens: Record<string, { at: number; entries: Record<string, Entry> }>;
  profiles: LayoutProfile[];
}

const STORE = 'inverse.layout';

/** Said on the window whenever the layout changes other than by a drag. */
export const LAYOUT_EVENT = 'inverse:layout';

/** Kept inside the screen by this much, the same margin dragging keeps. */
const MARGIN = 8;

/* ---------------------------------------------------------------------------
   The rule
   --------------------------------------------------------------------------- */

/**
 * Where a window goes on one screen, given where it was on another.
 *
 * Along each axis separately: a window in the first third keeps its distance
 * from that edge, one in the last third keeps its distance from the far edge,
 * and one in the middle third keeps its place as a share of the screen. Its own
 * size can have changed since, a bank grown by a row say, and the edge it was
 * held by is the one that stays put.
 */
export function adaptRect(
  was: Rect,
  from: Screen,
  to: Screen,
  now: Screen = was,
): { x: number; y: number } {
  const along = (
    start: number,
    size: number,
    span: number,
    target: number,
    current: number,
  ): number => {
    const middle = (start + size / 2) / Math.max(1, span);
    if (middle < 1 / 3) return start;
    if (middle > 2 / 3) return target - (span - (start + size)) - current;
    return middle * target - current / 2;
  };

  // Whole pixels, so a window put back on the screen it was made on lands on
  // exactly the pixel it was left on rather than a rounding error beside it.
  return {
    x: Math.round(along(was.x, was.width, from.width, to.width, now.width)),
    y: Math.round(along(was.y, was.height, from.height, to.height, now.height)),
  };
}

/** The same, for a size: kept, unless the screen is now too small for it. */
export function adaptSize(was: Screen, to: Screen): Screen {
  return {
    width: Math.min(was.width, Math.max(1, to.width - MARGIN * 2)),
    height: Math.min(was.height, Math.max(1, to.height - MARGIN * 2)),
  };
}

/* ---------------------------------------------------------------------------
   Storage
   --------------------------------------------------------------------------- */

function screenNow(): Screen {
  return { width: window.innerWidth, height: window.innerHeight };
}

/**
 * Which size of screen this is, as layouts are filed.
 *
 * Rounded to the nearest hundred pixels, so a browser window nudged by a few
 * pixels is still the same layout rather than a new one to arrange.
 */
export function screenKey(screen: Screen = screenNow()): string {
  return `${Math.round(screen.width / 100)}x${Math.round(screen.height / 100)}`;
}

export function orientationOf(screen: Screen = screenNow()): 'wide' | 'tall' {
  return screen.width >= screen.height ? 'wide' : 'tall';
}

function load(): Store {
  try {
    const raw = window.localStorage.getItem(STORE);
    if (!raw) return { screens: {}, profiles: [] };
    const held = JSON.parse(raw) as Partial<Store>;
    return {
      screens: typeof held.screens === 'object' && held.screens ? held.screens : {},
      profiles: Array.isArray(held.profiles) ? held.profiles : [],
    };
  } catch {
    // A layout that cannot be read is a layout to start again from.
    return { screens: {}, profiles: [] };
  }
}

function save(store: Store): void {
  try {
    window.localStorage.setItem(STORE, JSON.stringify(store));
  } catch {
    // Private browsing or a full quota: the layout lasts as long as the page.
  }
}

function announce(): void {
  window.dispatchEvent(new Event(LAYOUT_EVENT));
}

/**
 * Where an entry comes from on this screen, in order of how sure it is.
 *
 * This size of screen first, which is what somebody arranged here. Then a named
 * layout meant for screens this shape. Then whatever size was arranged most
 * recently, worked out for this one.
 */
function find(store: Store, key: string, screen: Screen): Entry | null {
  const here = store.screens[screenKey(screen)]?.entries[key];
  if (here) return here;

  const shape = orientationOf(screen);
  const auto = store.profiles.find((one) => one.auto === shape)?.entries[key];
  if (auto) return auto;

  let best: { at: number; entry: Entry } | null = null;
  for (const held of Object.values(store.screens)) {
    const entry = held.entries[key];
    if (entry && (!best || held.at > best.at)) best = { at: held.at, entry };
  }
  return best?.entry ?? null;
}

/** Layouts kept before this, one entry per window, so nobody loses theirs. */
function legacyPosition(kind: string): { x: number; y: number } | null {
  try {
    const raw =
      window.localStorage.getItem(`inverse.window.${kind}@${screenKey()}`) ??
      window.localStorage.getItem(`inverse.window.${kind}`);
    if (!raw) return null;
    const held = JSON.parse(raw) as Partial<{ x: number; y: number }>;
    return typeof held.x === 'number' && typeof held.y === 'number'
      ? { x: held.x, y: held.y }
      : null;
  } catch {
    return null;
  }
}

function legacySize(kind: string): Screen | null {
  try {
    const raw = window.localStorage.getItem(`inverse.window.size.${kind}`);
    if (!raw) return null;
    const held = JSON.parse(raw) as Partial<Screen>;
    return typeof held.width === 'number' && typeof held.height === 'number'
      ? { width: held.width, height: held.height }
      : null;
  } catch {
    return null;
  }
}

/* ---------------------------------------------------------------------------
   Windows
   --------------------------------------------------------------------------- */

/** Where a window goes on this screen, or nothing if it has never been put anywhere. */
export function positionFor(kind: string, size: Screen): { x: number; y: number } | null {
  const screen = screenNow();
  const entry = find(load(), `pos:${kind}`, screen);

  if (entry && 'x' in entry) {
    return adaptRect(entry, { width: entry.screenW, height: entry.screenH }, screen, size);
  }
  return legacyPosition(kind);
}

/** A window put somewhere by hand, kept for this size of screen. */
export function keepPosition(kind: string, at: { x: number; y: number }, size: Screen): void {
  const screen = screenNow();
  const store = load();
  const key = screenKey(screen);
  const held = store.screens[key] ?? { at: 0, entries: {} };

  held.entries[`pos:${kind}`] = {
    x: Math.round(at.x),
    y: Math.round(at.y),
    width: Math.round(size.width),
    height: Math.round(size.height),
    screenW: screen.width,
    screenH: screen.height,
  };
  held.at = Date.now();
  store.screens[key] = held;
  save(store);
}

/** How big a window is on this screen, or nothing if it has never been resized. */
export function sizeFor(kind: string): Screen | null {
  const screen = screenNow();
  const entry = find(load(), `size:${kind}`, screen);
  const size = entry ? { width: entry.width, height: entry.height } : legacySize(kind);
  return size ? adaptSize(size, screen) : null;
}

/** A window resized by hand, kept for this size of screen. */
export function keepSize(kind: string, size: Screen): void {
  const screen = screenNow();
  const store = load();
  const key = screenKey(screen);
  const held = store.screens[key] ?? { at: 0, entries: {} };

  held.entries[`size:${kind}`] = {
    width: Math.round(size.width),
    height: Math.round(size.height),
    screenW: screen.width,
    screenH: screen.height,
  };
  held.at = Date.now();
  store.screens[key] = held;
  save(store);
}

/* ---------------------------------------------------------------------------
   Named layouts
   --------------------------------------------------------------------------- */

export function profiles(): LayoutProfile[] {
  return load().profiles;
}

/** Whether this size of screen has an arrangement of its own. */
export function arrangedHere(): boolean {
  const held = load().screens[screenKey()];
  return held !== undefined && Object.keys(held.entries).length > 0;
}

/**
 * Keeps the layout on the screen now under a name.
 *
 * Everything as it stands on this screen, whether it was put here by hand or
 * worked out from another size, because what is being kept is what is being
 * looked at. Windows that are shut are kept too, where they would open.
 */
export function keepProfile(name: string): void {
  const screen = screenNow();
  const store = load();
  const kinds = new Set<string>();
  for (const held of Object.values(store.screens)) {
    for (const key of Object.keys(held.entries)) kinds.add(key);
  }
  for (const profile of store.profiles) {
    for (const key of Object.keys(profile.entries)) kinds.add(key);
  }

  const entries: Record<string, Entry> = {};
  for (const key of kinds) {
    const entry = find(store, key, screen);
    if (!entry) continue;

    if ('x' in entry) {
      const at = adaptRect(entry, { width: entry.screenW, height: entry.screenH }, screen);
      entries[key] = {
        ...entry,
        x: Math.round(at.x),
        y: Math.round(at.y),
        screenW: screen.width,
        screenH: screen.height,
      };
    } else {
      entries[key] = { ...adaptSize(entry, screen), screenW: screen.width, screenH: screen.height };
    }
  }

  const kept = store.profiles.filter((one) => one.name !== name);
  const before = store.profiles.find((one) => one.name === name);
  kept.push({ name, auto: before?.auto ?? null, entries });
  store.profiles = kept;
  save(store);
  announce();
}

/** Puts a named layout on this screen, as this screen's own. */
export function applyProfile(name: string): void {
  const store = load();
  const profile = store.profiles.find((one) => one.name === name);
  if (!profile) return;

  store.screens[screenKey()] = { at: Date.now(), entries: { ...profile.entries } };
  save(store);
  announce();
}

export function forgetProfile(name: string): void {
  const store = load();
  store.profiles = store.profiles.filter((one) => one.name !== name);
  save(store);
  announce();
}

/**
 * Uses a named layout on every wide screen, or every tall one, or neither.
 *
 * Only one of each: two layouts both claiming every wide screen would leave the
 * choice between them to whichever happened to be saved first.
 */
export function setProfileAuto(name: string, auto: LayoutAuto): void {
  const store = load();
  for (const profile of store.profiles) {
    if (profile.name === name) profile.auto = auto;
    else if (auto !== null && profile.auto === auto) profile.auto = null;
  }
  save(store);
  announce();
}

/**
 * Forgets what was arranged for this size of screen.
 *
 * Everything goes back to being worked out from other sizes, or to where it
 * starts if nothing has ever been arranged anywhere.
 */
export function forgetThisScreen(): void {
  const store = load();
  delete store.screens[screenKey()];
  save(store);
  announce();
}
